import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { AnimTap, EColors, EColors2, EFonts, EGrads, EHovers } from 'libs/styles/styling';
import React from 'react';
import styled, { css } from 'styled-components/macro';

interface Props {
    type:
        | 'primary'
        | 'primary2'
        | 'secondary'
        | 'editor'
        | 'grey'
        | 'white'
        | 'event'
        | 'event-secondary'
        | 'event-secondary2';
    iconSet?: IconPrefix;
    iconName?: IconName;
    disabled?: boolean;
    children: React.ReactNode;
    onClick?: () => void;
    className?: string;
    alignText?: 'center';
    backgroundColor?: string;
    textColor?: string;
    noOverflow?: boolean;
}

export const MainButton = (props: Props): JSX.Element => {
    const anim = !props.disabled ? AnimTap : {};

    return (
        <S_MainButton
            className={props.className}
            onClick={() => {
                if (!props.disabled) {
                    props.onClick?.();
                }
            }}
            $type={props.type}
            $disabled={props.disabled}
            $backgroundColor={props.backgroundColor}
            $textColor={props.textColor}
            {...anim}
        >
            {props.iconSet && props.iconName && (
                <S_Icon $type={props.type} icon={[props.iconSet, props.iconName]} />
            )}
            <S_Content $noOverflow={props.noOverflow} $align={props.alignText}>
                {props.children}
            </S_Content>
        </S_MainButton>
    );
};

const S_MainButton = styled(motion.div)<{
    $disabled: boolean;
    $type: Props['type'];
    $backgroundColor?: string;
    $textColor?: string;
}>`
    height: 30px;
    font-size: 13px;
    padding: 8px 12px;
    display: flex;
    text-align: center;
    width: fit-content;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 7px;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    font-family: ${EFonts.Medium};
    user-select: none;
    ${(props) => {
        if (props.$type === 'primary') {
            if (props.$disabled) {
                return css`
                    background: ${EColors.GREY3};
                    cursor: not-allowed;
                `;
            } else {
                return css`
                    color: ${EColors.DARK};
                    background: ${EGrads.ACC1_GRAD};
                    box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.3);
                    &:hover {
                        box-shadow: ${EHovers.LIGHT2}, 0px 10px 30px rgba(0, 0, 0, 0.3);
                    }
                `;
            }
        } else if (props.$type === 'primary2') {
            if (props.$disabled) {
                return css`
                    background: ${EColors.GREY3};
                    cursor: not-allowed;
                `;
            } else {
                return css`
                    color: ${EColors.DARK};
                    background: ${EGrads.ACC1_GRAD};
                    box-shadow: 5px 5px 10px 0px rgba(0, 0, 0, 0.1);
                    &:hover {
                        box-shadow: ${EHovers.LIGHT2}, 5px 5px 10px 0px rgba(0, 0, 0, 0.1);
                    }
                `;
            }
        } else if (props.$type === 'grey') {
            return css`
                color: ${EColors.ALMOST_BLACK};
                background: linear-gradient(270deg, #dadada 0%, #f7f7f7 100%);
                box-shadow: 3px 3px 10px rgba(0, 0, 0, 0.1);
                &:hover {
                    box-shadow: ${EHovers.LIGHT2}, 3px 3px 10px rgba(0, 0, 0, 0.1);
                }
            `;
        } else if (props.$type === 'secondary') {
            return css`
                color: ${EColors.ACC1_A};
                background-color: #313974;
                box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.3);
                &:hover {
                    box-shadow: ${EHovers.LIGHT1}, 0px 10px 30px rgba(0, 0, 0, 0.3);
                }
            `;
        } else if (props.$type === 'editor') {
            return css``;
        } else if (props.$type === 'white') {
            return css`
                color: ${EColors.WHITE};
                background-color: unset;
                border: 1px solid white;
                &:hover {
                    box-shadow: ${EHovers.LIGHT1};
                }
            `;
        } else if (props.$type === 'event') {
            return css`
                background-color: ${EColors2.DARK};
                border: unset;
                color: white;
                box-shadow: unset;
                border-radius: 100px;
                min-height: 45px;
                height: unset;
                font-size: 15px;
                padding: 10px 25px;
                width: fit-content;
                max-width: 350px;
                &:hover {
                    background-color: ${EColors2.GREY2};
                    color: ${EColors2.DARK};
                }
                @media only screen and (min-width: 1260px) {
                    max-width: 450px;
                }
            `;
        } else if (props.$type === 'event-secondary') {
            return css`
                background-color: ${EColors2.GREY2};
                border: unset;
                color: ${EColors2.DARK};
                box-shadow: unset;
                border-radius: 100px;
                min-height: 40px;
                height: unset;
                font-size: 15px;
                padding: 5px 22px;
                line-height: 21px;
                width: fit-content;
                max-width: 350px;
                &:hover {
                    background-color: ${EColors2.DARK};
                    color: white;
                }
                @media only screen and (min-width: 1260px) {
                    max-width: 450px;
                }
            `;
        } else if (props.$type === 'event-secondary2') {
            return css`
                background-color: ${EColors2.GREY1};
                border: unset;
                color: ${EColors2.DARK};
                box-shadow: unset;
                border-radius: 100px;
                min-height: 40px;
                height: unset;
                font-size: 15px;
                padding: 5px 22px;
                line-height: 21px;
                width: fit-content;
                max-width: 350px;
                &:hover {
                    background-color: ${EColors2.DARK};
                    color: white;
                }
                @media only screen and (min-width: 1260px) {
                    max-width: 450px;
                }
            `;
        }
    }}

    ${(props) => {
        if (props.$backgroundColor) {
            return css`
                background-color: ${props.$backgroundColor};
            `;
        }
    }}

${(props) => {
        if (props.$textColor) {
            return css`
                color: ${props.$textColor};
            `;
        }
    }}
`;

const S_Icon = styled(FontAwesomeIcon)<{ $type: string }>`
    margin: 0 10px 0 0;
    font-size: 13px;
    ${(props) => {
        if (props.$type === 'event') {
            return css`
                margin: 0 13px 0 0;
                font-size: 16px;
            `;
        } else if (props.$type === 'event-secondary') {
            return css`
                margin: 0 13px 0 0;
                font-size: 16px;
            `;
        } else if (props.$type === 'event-secondary2') {
            return css`
                margin: 0 13px 0 0;
                font-size: 16px;
            `;
        }
    }}
`;

const S_Content = styled.div<{ $align?: 'center'; $noOverflow: boolean }>`
    display: inline-flex;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ${(props) => (props.$noOverflow ? 'unset' : 'ellipsis')};
    ${(props) => {
        if (props.$align === 'center') {
            return css`
                justify-content: center;
            `;
        }
    }}
`;
